 'use client';

import React, { useState, useEffect } from 'react';
import { apiService } from '@/lib/api';
import { useDate } from '@/contexts/DateContext';
import { Calendar, MapPin, Loader2, Bell, Pencil, Trash2 } from 'lucide-react';
import { extractPanchangaValue, extractPanchangaValueIntl } from '@/lib/safeRender';
import EventsSidebar from './EventsSidebar';

type ChaughadiPeriod = { en: string; np: string; type: 'good' | 'bad'; start: string; end: string; startM: number; endM: number; isCurrent: boolean };

// Shared chaughadi tables — used by both the Chaughadi tab and the Subha Saait
// (Shubha/Ashubha Bela) sub-tabs. Keep a single source of truth.
const CHAUGHADI_NAMES: Record<string, { en: string; np: string; type: 'good' | 'bad' }> = {
  Udveg:  { en: 'Udveg',  np: 'उद्वेग', type: 'bad' },
  Chara:  { en: 'Chara',  np: 'चर',    type: 'bad' },
  Labh:   { en: 'Labh',   np: 'लाभ',   type: 'good' },
  Amrit:  { en: 'Amrit',  np: 'अमृत',  type: 'good' },
  Kaal:   { en: 'Kaal',   np: 'काल',   type: 'bad' },
  Shubh:  { en: 'Shubh',  np: 'शुभ',   type: 'good' },
  Rog:    { en: 'Rog',    np: 'रोग',   type: 'bad' },
};
// Exact sequences per weekday (Sun=0..Sat=6)
const DAY_SEQ: string[][] = [
  ['Udveg','Chara','Labh','Amrit','Kaal','Shubh','Rog','Udveg'],       // Sun
  ['Amrit','Kaal','Shubh','Rog','Udveg','Chara','Labh','Amrit'],       // Mon
  ['Rog','Udveg','Chara','Labh','Amrit','Kaal','Shubh','Rog'],         // Tue
  ['Labh','Amrit','Kaal','Shubh','Rog','Udveg','Chara','Labh'],        // Wed
  ['Shubh','Rog','Udveg','Chara','Labh','Amrit','Kaal','Shubh'],       // Thu
  ['Chara','Labh','Amrit','Kaal','Shubh','Rog','Udveg','Chara'],       // Fri
  ['Kaal','Shubh','Rog','Udveg','Chara','Labh','Amrit','Kaal'],        // Sat
];
const NIGHT_SEQ: string[][] = [
  ['Shubh','Amrit','Chara','Rog','Kaal','Labh','Udveg','Shubh'],       // Sun
  ['Chara','Rog','Kaal','Labh','Udveg','Shubh','Amrit','Chara'],       // Mon
  ['Kaal','Labh','Udveg','Shubh','Amrit','Chara','Rog','Kaal'],        // Tue
  ['Udveg','Shubh','Amrit','Chara','Rog','Kaal','Labh','Udveg'],       // Wed
  ['Amrit','Chara','Rog','Kaal','Labh','Udveg','Shubh','Amrit'],       // Thu
  ['Rog','Kaal','Labh','Udveg','Shubh','Amrit','Chara','Rog'],         // Fri
  ['Labh','Udveg','Shubh','Amrit','Chara','Rog','Kaal','Labh'],        // Sat
];

const pad2 = (n: number) => String(n).padStart(2, '0');
const fmtMin = (m: number) => { const mm = ((m % (24*60)) + 24*60) % (24*60); return `${pad2(Math.floor(mm/60))}:${pad2(mm%60)}`; };

// Day chaughadi periods (sunrise -> sunset, 8 equal parts)
function computeDayChaughadi(srMin: number, ssMin: number, dayOfWeek: number) {
  const partLen = (ssMin - srMin) / 8;
  return DAY_SEQ[dayOfWeek].map((name, i) => {
    const startM = Math.round(srMin + i * partLen);
    const endM = Math.round(srMin + (i + 1) * partLen);
    return { ...CHAUGHADI_NAMES[name], start: fmtMin(startM), end: fmtMin(endM), startM, endM };
  });
}

function ChaughadiPanel({ isNep, dayPeriods, nightPeriods, autoTab, currentDayIdx, currentNightIdx, varaLabel }: {
  isNep: boolean; dayPeriods: ChaughadiPeriod[]; nightPeriods: ChaughadiPeriod[];
  autoTab: 'day' | 'night'; currentDayIdx: number; currentNightIdx: number; varaLabel: string;
}) {
  const [subTab, setSubTab] = useState<'day' | 'night'>(autoTab);
  const currentRef = React.useRef<HTMLDivElement>(null);
  const nepNum = (s: string) => s.replace(/[0-9]/g, d => '०१२३४५६७८९'[Number(d)]);
  const fmtT = (s: string) => isNep ? nepNum(s) : s;

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [subTab]);

  const periods = subTab === 'day' ? dayPeriods : nightPeriods;
  const currentIdx = subTab === 'day' ? currentDayIdx : currentNightIdx;
  const currentPeriod = currentIdx >= 0 ? periods[currentIdx] : null;

  return (
    <div>
      {/* Current chaughadi banner */}
      {currentPeriod && (
        <div className={`px-3 sm:px-4 py-3 flex items-center justify-between ${currentPeriod.type === 'good' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full animate-pulse ${currentPeriod.type === 'good' ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{isNep ? 'हालको चौघडी' : 'Current Chaughadi'}</div>
              <span className={`text-base font-bold ${currentPeriod.type === 'good' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'} ${isNep ? 'devanagari' : ''}`}>{isNep ? currentPeriod.np : currentPeriod.en}</span>
            </div>
          </div>
          <div className="text-right">
            <span className={`text-sm font-semibold ${currentPeriod.type === 'good' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>{fmtT(currentPeriod.start)} - {fmtT(currentPeriod.end)}</span>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{currentPeriod.type === 'good' ? (isNep ? 'शुभ' : 'Auspicious') : (isNep ? 'अशुभ' : 'Inauspicious')}</div>
          </div>
        </div>
      )}

      {/* Vara (astrological day) label */}
      <div className="px-3 sm:px-4 py-1.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-dark-800/50 border-b border-gray-200 dark:border-gray-700">
        <span className={isNep ? 'devanagari' : ''}>{varaLabel}</span>
      </div>

      {/* Day / Night tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button onClick={() => setSubTab('day')} className={`flex-1 py-2 text-xs sm:text-sm font-bold uppercase tracking-wide transition-colors border-b-2 ${subTab === 'day' ? 'border-orange-500 text-orange-700 dark:text-orange-400 bg-orange-50/50 dark:bg-orange-900/20' : 'border-transparent text-gray-500 dark:text-gray-400'}`}>
          {isNep ? '☀️ दिन' : '☀️ Day'}
        </button>
        <button onClick={() => setSubTab('night')} className={`flex-1 py-2 text-xs sm:text-sm font-bold uppercase tracking-wide transition-colors border-b-2 ${subTab === 'night' ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20' : 'border-transparent text-gray-500 dark:text-gray-400'}`}>
          {isNep ? '🌙 रात' : '🌙 Night'}
        </button>
      </div>

      {/* Period list */}
      {periods.map((p, i) => {
        const isCur = i === currentIdx;
        return (
          <div key={i} ref={isCur ? currentRef : undefined} className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50 transition-colors ${isCur ? (p.type === 'good' ? 'bg-green-100 dark:bg-green-900/30 ring-1 ring-inset ring-green-400/50' : 'bg-red-100 dark:bg-red-900/30 ring-1 ring-inset ring-red-400/50') : p.type === 'good' ? 'bg-green-50/30 dark:bg-green-900/5' : ''}`}>
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isCur ? 'animate-pulse' : ''} ${p.type === 'good' ? 'bg-green-500' : 'bg-red-400'}`}></span>
            <span className={`text-lg font-bold flex-1 ${isCur ? (p.type === 'good' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200') : p.type === 'good' ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'} ${isNep ? 'devanagari' : ''}`}>
              {isNep ? p.np : p.en}
              {isCur && <span className="ml-1.5 text-xs font-bold uppercase tracking-wider opacity-70">← {isNep ? 'अहिले' : 'NOW'}</span>}
            </span>
            <span className={`text-base font-semibold ${isCur ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{fmtT(p.start)} - {fmtT(p.end)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PanchangaSidebar() {
  const { dateData, loading: contextLoading } = useDate();
  const [displayLanguage, setDisplayLanguage] = useState<'nepali' | 'english'>('nepali');
  const [loading, setLoading] = useState(true);
  const [panchanga, setPanchanga] = useState<any | null>(null);
  const [nepaliDate, setNepaliDate] = useState<any | null>(null);
  const [englishDate, setEnglishDate] = useState<any | null>(null);
  const [englishWeekday, setEnglishWeekday] = useState<string>('');
  const [nepaliWeekday, setNepaliWeekday] = useState<string>('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'panchanga' | 'muhurta' | 'chaughadi' | 'eclipse'>('panchanga');
  const [eclipseSubTab, setEclipseSubTab] = useState<'surya' | 'chandra' | 'past'>('surya');
  const [muhurtaSubTab, setMuhurtaSubTab] = useState<'shubha' | 'ashubha' | 'agni' | 'rudri' | 'annaprashan' | 'grihaNirman' | 'grihaPrabesh' | 'asta'>('shubha');
  const [planetAstaRows, setPlanetAstaRows] = useState<any[]>([]);
  const [expandedEclipse, setExpandedEclipse] = useState<string | null>(null);
  const [showReminders, setShowReminders] = useState(false);
  const [reminders, setReminders] = useState<any[]>([]);
  const [editingReminderId, setEditingReminderId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [editTime, setEditTime] = useState('09:00');
  const [ceremonyMuhurtas, setCeremonyMuhurtas] = useState<any[]>([]);
  const [todaySunrise, setTodaySunrise] = useState<string | null>(null);

  const pad = (n: number) => String(n).padStart(2, '0');
  const englishWeekdays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const nepaliWeekdays = ['आइतबार','सोमबार','मंगलबार','बुधबार','बिहिबार','शुक्रबार','शनिबार'];

  const toNepaliNumerals = (s: string) => {
    const map: { [k: string]: string } = { '0':'०','1':'१','2':'२','3':'३','4':'४','5':'५','6':'६','7':'७','8':'८','9':'९' };
    return String(s).split('').map(ch => (ch >= '0' && ch <= '9') ? map[ch] || ch : ch).join('');
  };

  const getMonthShortName = (month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || '';
  };

  const translateRahuPeriod = (word: string) => {
    if (!word) return '';
    const w = String(word).toLowerCase();
    if (w.includes('morning') || w.includes('am') || w.includes('बिहान')) return 'बिहान';
    if (w.includes('afternoon') || w.includes('pm') || w.includes('दिउँसो')) return 'दिउँसो';
    if (w.includes('evening') || w.includes('साँझ')) return 'साँझ';
    if (w.includes('night') || w.includes('रात')) return 'राति';
    return word;
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem('panchangaDisplayLanguage');
      if (saved === 'nepali' || saved === 'english') setDisplayLanguage(saved);
    } catch (e) {
      // ignore
    }
  }, []);

  // Listen for calendar date selection events
  useEffect(() => {
    const handleDateSelected = (event: CustomEvent) => {
      const { nepali, english } = event.detail;

      // Update local state for sidebar display
      if (nepali) {
        setNepaliDate({
          year: nepali.year,
          month: nepali.month,
          day: nepali.day,
          monthName: nepali.monthName || ''
        });
      }

      if (english) {
        setEnglishDate({
          year: english.year,
          month: english.month,
          day: english.day
        });
      }

      // Calculate weekday
      if (english) {
        const date = new Date(english.year, english.month - 1, english.day);
        const dayIndex = date.getDay();
        setEnglishWeekday(englishWeekdays[dayIndex]);
        setNepaliWeekday(nepaliWeekdays[dayIndex]);
      }

      // Fetch panchanga data for the selected date
      fetchPanchangaData(nepali, userLocation || undefined);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('calendar:dateSelected', handleDateSelected as EventListener);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('calendar:dateSelected', handleDateSelected as EventListener);
      }
    };
  }, []);

  // Zodiac signs (Rashi) mapping
  const rashiMap: Record<string, { nep: string; en: string }> = {
    mesha: { nep: 'मेष', en: 'Mesha' },
    vrishabha: { nep: 'वृषभ', en: 'Vrishabha' },
    mithuna: { nep: 'मिथुन', en: 'Mithuna' },
    karka: { nep: 'कर्क', en: 'Karka' },
    simha: { nep: 'सिंह', en: 'Simha' },
    kanya: { nep: 'कन्या', en: 'Kanya' },
    tula: { nep: 'तुला', en: 'Tula' },
    vrishchika: { nep: 'वृश्चिक', en: 'Vrishchika' },
    dhanu: { nep: 'धनु', en: 'Dhanu' },
    makara: { nep: 'मकर', en: 'Makara' },
    kumbha: { nep: 'कुम्भ', en: 'Kumbha' },
    meena: { nep: 'मीन', en: 'Meena' }
  };

  // Special festivals mapping
  const festivalMap: Record<string, { nep: string; en: string }> = {
    // Add common festival mappings here
    // This can be expanded based on actual backend data
  };

  // Auspicious occasions (Shubha Saait) mapping
  const shubhaMap: Record<string, { nep: string; en: string }> = {
    rudri: { nep: 'रुद्री', en: 'Rudri' },
    griha_brabesh: { nep: 'गृह प्रवेश', en: 'Griha Prabesh' },
    namakarana: { nep: 'नामकरण', en: 'Namakarana' },
    grihapravesh: { nep: 'गृहप्रवेश', en: 'GrihaPravesh' },
    chudakarna: { nep: 'चूडाकरण', en: 'Chudakarna' },
    vidyarambha: { nep: 'विद्यारम्भ', en: 'Vidyarambha' },
    vratabandha: { nep: 'व्रतबन्ध', en: 'Vratabandha' },
    betabandha: { nep: 'व्रतबन्ध', en: 'Vratabandha' },
    agni: { nep: 'अग्नि', en: 'Agni' },
    annaprassan: { nep: 'अन्नप्रसन', en: 'Annaprassan' },
    jatakarma: { nep: 'जातकर्म', en: 'Jatakarma' },
    upanayana: { nep: 'उपनयन', en: 'Upanayana' },
    karnabheda: { nep: 'कर्णभेद', en: 'Karnabheda' },
    mukhpraveshan: { nep: 'मुखप्रवेशन', en: 'Mukhpraveshan' },
    vivaha: { nep: 'विवाह', en: 'Vivaha' },
    griha_rambha: { nep: 'गृहरम्भा', en: 'Griha Arambha' }
  };

  const formatRahuKaalForDisplay = (raw: any, lang: 'nepali' | 'english') => {
    if (!raw) return '';
    const s = String(raw).trim();
    // Extract time range like 10:59-11:59
    const timeMatch = s.match(/\d{1,2}:\d{2}(?:-\d{1,2}:\d{2})?/);
    const periodMatch = s.match(/(Morning|Afternoon|Evening|Night|AM|PM|बिहान|दिउँसो|साँझ|राति)/i);
    const timePart = timeMatch ? timeMatch[0] : s.split(' ')[0] || s;
    const periodPart = periodMatch ? periodMatch[0] : (s.split(' ').slice(1).join(' ') || '');

    if (lang === 'nepali') {
      const nepTime = toNepaliNumerals(timePart);
      const nepPeriod = translateRahuPeriod(periodPart) || '';
      return `${nepTime}${nepPeriod ? ' ' + nepPeriod : ''}`;
    }

    // default English
    return `${timePart}${periodPart ? ' ' + periodPart : ''}`;
  };

  // Localize Rashi (zodiac sign) values
  const localizeRashi = (rashiValue: any, lang: 'nepali' | 'english') => {
    if (!rashiValue) return '';

    // Handle object format from backend {name, nameEnglish, nameNepali}
    if (typeof rashiValue === 'object' && rashiValue !== null) {
      if (lang === 'nepali') {
        return rashiValue.nameNepali || rashiValue.name || rashiValue.nameEnglish || '';
      } else {
        return rashiValue.nameEnglish || rashiValue.name || rashiValue.nameNepali || '';
      }
    }

    // Handle string format - normalize and map
    const rashi = String(rashiValue).toLowerCase().trim();
    const mapping = rashiMap[rashi];
    if (mapping) {
      return lang === 'nepali' ? mapping.nep : mapping.en;
    }

    // If not found in mapping, try to use extractPanchangaValueIntl as fallback
    return extractPanchangaValueIntl(rashiValue, lang);
  };

  // Localize festival values
  const localizeFestival = (festivalValue: any, lang: 'nepali' | 'english') => {
    if (!festivalValue) return '';
    const festival = String(festivalValue).toLowerCase().trim();
    const mapping = festivalMap[festival];
    if (mapping) {
      return lang === 'nepali' ? mapping.nep : mapping.en;
    }
    return String(festivalValue);
  };

  // Localize Shubha Saait (auspicious occasions) values
  const localizeShubhaSaait = (saaitValue: any, lang: 'nepali' | 'english') => {
    if (!saaitValue) return '';
    const saaitStr = String(saaitValue).trim();

    // Handle comma-separated values
    const saaits = saaitStr.split(',').map(s => s.trim().toLowerCase());

    // Filter out invalid entries like 'bibha'
    const validSaaits = saaits.filter(saait => saait && saait !== 'bibha' && saait !== '');

    const localizedSaaits = validSaaits.map(saait => {
      const mapping = shubhaMap[saait];
      if (mapping) {
        return lang === 'nepali' ? mapping.nep : mapping.en;
      }
      return saait; // Return original if not found
    });

    return localizedSaaits.join(', ');
  };

  const stripTZ = (s: string) => String(s || '').replace(/\s*[+-]\d{4}\s*$/g, '').trim();

  // Nepali time-of-day period based on sunset:
  //   पछिल्लो रात (after midnight) → राति
  //   बिहान (dawn – 11 AM) → बिहान
  //   दिउँसो (11 AM – 1h before sunset) → दिउँसो
  //   साँझ / सन्ध्या (±1h around sunset) → साँझ
  //   राति (1h after sunset onward) → राति
  const getSunsetMin = () => {
    const ss = panchanga?.sunset || panchanga?.sunsetTime;
    if (ss) {
      const m = String(ss).match(/(\d{1,2}):(\d{2})/);
      if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    }
    return 18 * 60; // fallback 6:00 PM
  };
  // Formal Sanskrit-register period words used across the panchang:
  // विहान (dawn/morning) · दिवा (day) · साँझ (evening) · रात्री (night)
  const nepPeriodWord = (hh: number, mm: number) => {
    const t = hh * 60 + mm;
    const sunset = getSunsetMin();
    if (t < 4 * 60) return 'रात्री';
    if (t < 11 * 60) return 'विहान';
    if (t >= sunset - 60 && t <= sunset + 60) return 'साँझ';
    if (t > sunset + 60) return 'रात्री';
    return 'दिवा';
  };
  const formatTimeWithPeriod = (rawTime: any, lang: 'nepali' | 'english', preferSunrise=false) => {
    if (!rawTime) return '';
    let s = String(rawTime).trim();
    // Accept formats: HH:MM:SS, HH:MM, H:MM, HH:MM-HH:MM
    // If range, show first part
    if (s.includes('-')) s = s.split('-')[0];
    const parts = s.split(':');
    if (parts.length < 2) return s;
    let hh = parseInt(parts[0], 10);
    const mm = parts[1];
    const period = hh >= 12 ? 'PM' : 'AM';
    const displayHour = ((hh + 11) % 12) + 1; // convert 0->12,13->1
    const timeDisplay = `${String(displayHour).padStart(2,'0')}:${mm}`;

    if (lang === 'nepali') {
      const nepTime = toNepaliNumerals(timeDisplay);
      const nepPeriod = nepPeriodWord(hh, parseInt(mm, 10));
      return `${nepPeriod} ${nepTime}`;
    }
    return `${timeDisplay} ${period}`;
  };

  const enableGPS = () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setUserLocation(coords);
        setGpsLoading(false);
        loadTodayPanchanga(coords);
      },
      () => setGpsLoading(false),
      { timeout: 8000 }
    );
  };

  const fetchPanchangaData = async (dateObj: any, coords?: { lat: number; lon: number }) => {
    if (!dateObj) return;

    try {
      let dateStr = '';
      if (dateObj.english && typeof dateObj.english === 'object') {
        const e = dateObj.english;
        dateStr = `${e.year}-${pad(e.month)}-${pad(e.day)}`;
      } else {
        const nepIso = `${dateObj.year}-${pad(dateObj.month)}-${pad(dateObj.day)}`;
        try {
          const conv: any = await apiService.convertDate('nepali','english', nepIso);
          if (conv?.success && conv?.data?.english) {
            const ey = conv.data.english.year;
            const em = String(conv.data.english.month).padStart(2,'0');
            const ed = String(conv.data.english.day).padStart(2,'0');
            dateStr = `${ey}-${em}-${ed}`;
          }
        } catch (convError) {
          console.warn('Failed to convert Nepali date for panchanga:', convError);
        }
      }

      if (dateStr) {
        const resp: any = await apiService.getPanchanga(dateStr, coords || userLocation || undefined);
        if (resp?.success && resp?.data) {
          setPanchanga(resp.data);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch panchanga for selected date:', error);
    }
  };

  const loadTodayPanchanga = async (coords?: { lat: number; lon: number }) => {
    setLoading(true);
    try {
      if (dateData?.nepali) setNepaliDate(dateData.nepali);
      if (dateData?.english) setEnglishDate(dateData.english);

      let dateStr = '';
      if (dateData?.english && typeof dateData.english === 'object') {
        const e = dateData.english as any;
        dateStr = `${e.year}-${pad(e.month)}-${pad(e.day)}`;
      } else if (dateData?.nepali && typeof dateData.nepali === 'object') {
        const n = dateData.nepali as any;
        dateStr = `${n.year}-${pad(n.month)}-${pad(n.day)}`;
      }

      const loc = coords || userLocation || undefined;

      if (!dateStr) {
        const resp: any = await apiService.getCurrentPanchanga(loc);
        if (resp?.success && resp?.data) setPanchanga(resp.data);
        return;
      }

      try {
        if (dateData?.english && typeof dateData.english === 'object') {
          const e = dateData.english as any;
          const iso = `${e.year}-${pad(e.month)}-${pad(e.day)}`;
          const dayIndex = new Date(iso).getDay();
          setEnglishWeekday(englishWeekdays[dayIndex]);
          setNepaliWeekday(nepaliWeekdays[dayIndex]);
        } else if (dateData?.nepali && typeof dateData.nepali === 'object') {
          const n = dateData.nepali as any;
          const nepIso = `${n.year}-${pad(n.month)}-${pad(n.day)}`;
          try {
            const conv: any = await apiService.convertDate('nepali','english', nepIso);
            if (conv?.success && conv?.data?.english) {
              const ey = conv.data.english.year;
              const em = String(conv.data.english.month).padStart(2,'0');
              const ed = String(conv.data.english.day).padStart(2,'0');
              const iso = `${ey}-${em}-${ed}`;
              const dayIndex = new Date(iso).getDay();
              setEnglishWeekday(englishWeekdays[dayIndex]);
              setNepaliWeekday(nepaliWeekdays[dayIndex]);
            }
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }

      try {
        const panchangaResponse: any = await apiService.getPanchanga(dateStr, loc);
        if (panchangaResponse?.success && panchangaResponse?.data) {
          setPanchanga(panchangaResponse.data);
          if (!todaySunrise) {
            setTodaySunrise(panchangaResponse.data.sunrise || panchangaResponse.data.sunriseTime || null);
          }
        } else {
          const fallback: any = await apiService.getCurrentPanchanga(loc);
          if (fallback?.success && fallback?.data) setPanchanga(fallback.data);
        }
      } catch (err) {
        try {
          const fallback: any = await apiService.getCurrentPanchanga(loc);
          if (fallback?.success && fallback?.data) setPanchanga(fallback.data);
        } catch (e) {}
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateData) loadTodayPanchanga();
    else if (!contextLoading) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateData, contextLoading]);

  const refreshReminders = () => {
    try {
      const raw = localStorage.getItem('swasti_calendar_reminders_v1');
      if (!raw) { setReminders([]); return; }
      const all = JSON.parse(raw);
      if (!Array.isArray(all)) { setReminders([]); return; }
      const now = new Date();
      const future = all.filter((item: any) => {
        const dt = new Date(`${item.englishDate}T${item.reminderTime || '09:00'}`);
        return dt >= now;
      }).sort((a: any, b: any) => new Date(`${a.englishDate}T${a.reminderTime || '09:00'}`).getTime() - new Date(`${b.englishDate}T${b.reminderTime || '09:00'}`).getTime());
      setReminders(future);
    } catch {}
  };

  useEffect(() => {
    const n = nepaliDate as any;
    if (!n || !n.year || !n.month) { setCeremonyMuhurtas([]); setPlanetAstaRows([]); return; }
    let cancelled = false;
    (async () => {
      try {
        // Fetch all ceremony entries for the viewed Nepali year + month
        const resp: any = await apiService.getCeremonyMuhurta({ year: n.year, month: n.month });
        if (!cancelled && resp?.success) setCeremonyMuhurtas(resp.data || []);
      } catch { if (!cancelled) setCeremonyMuhurtas([]); }

      // Fetch planet asta entries active on the viewed date
      try {
        const iso = `${n.year}-${pad(n.month)}-${pad(n.day || 1)}`;
        const resp2: any = await apiService.getPlanetAstaCurrent(iso);
        if (!cancelled && resp2?.success) setPlanetAstaRows(resp2.data || []);
      } catch { if (!cancelled) setPlanetAstaRows([]); }
    })();
    return () => { cancelled = true; };
  }, [nepaliDate]);

  useEffect(() => {
    refreshReminders();
    const onUpdated = () => refreshReminders();
    if (typeof window !== 'undefined') {
      window.addEventListener('calendar:remindersUpdated', onUpdated);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('calendar:remindersUpdated', onUpdated);
      }
    };
  }, []);

  const deleteReminder = (id: string) => {
    try {
      const raw = localStorage.getItem('swasti_calendar_reminders_v1');
      const all = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(all) ? all.filter((r: any) => r.id !== id) : [];
      localStorage.setItem('swasti_calendar_reminders_v1', JSON.stringify(next));
      if (editingReminderId === id) setEditingReminderId(null);
      refreshReminders();
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new CustomEvent('calendar:remindersUpdated')); } catch {}
      }
    } catch {}
  };

  const startEditReminder = (item: any) => {
    setEditingReminderId(item.id);
    setEditNote(item.note || '');
    setEditTime(item.reminderTime || '09:00');
  };

  const saveReminderEdit = (id: string) => {
    try {
      const raw = localStorage.getItem('swasti_calendar_reminders_v1');
      const all = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(all) ? all.map((r: any) => r.id === id ? { ...r, note: editNote, reminderTime: editTime, updatedAt: new Date().toISOString() } : r) : [];
      localStorage.setItem('swasti_calendar_reminders_v1', JSON.stringify(next));
      setEditingReminderId(null);
      refreshReminders();
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new CustomEvent('calendar:remindersUpdated')); } catch {}
      }
    } catch {}
  };


  return (
    <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 w-full">
      <div className="flex items-center gap-2 mb-4 justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {displayLanguage === 'nepali' ? 'आजको पंचांग' : "Today's Panchanga"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowReminders(!showReminders)}
              title="Reminders"
              aria-label="Reminders"
              className={`p-1.5 rounded-lg transition-colors relative ${
                showReminders
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                  : 'text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Bell className="w-4 h-4" />
              {reminders.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{reminders.length > 9 ? '9+' : reminders.length}</span>
              )}
            </button>
            {showReminders && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-dark-700 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-50 overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 dark:bg-dark-600 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">{displayLanguage === 'nepali' ? 'रिमाइन्डरहरू' : 'Reminders'}</span>
                  <button onClick={() => setShowReminders(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm">✕</button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {reminders.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-6">{displayLanguage === 'nepali' ? 'कुनै रिमाइन्डर छैन' : 'No upcoming reminders'}</p>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-600">
                      {reminders.map((item: any) => (
                        <div key={item.id} className="px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-600/50 group">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">{item.nepaliDate}</span>
                            <span className="text-[10px] text-gray-400">{item.englishDate} • {editingReminderId === item.id ? '' : item.reminderTime}</span>
                          </div>
                          {editingReminderId === item.id ? (
                            <div className="mt-1.5 space-y-1.5">
                              <textarea
                                value={editNote}
                                onChange={(e) => setEditNote(e.target.value)}
                                rows={2}
                                className="w-full text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-800 text-gray-800 dark:text-gray-200"
                                placeholder={displayLanguage === 'nepali' ? 'रिमाइन्डर नोट' : 'Reminder note'}
                              />
                              <div className="flex items-center gap-2">
                                <input
                                  type="time"
                                  value={editTime}
                                  onChange={(e) => setEditTime(e.target.value)}
                                  className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-800 text-gray-800 dark:text-gray-200"
                                />
                                <button onClick={() => saveReminderEdit(item.id)} className="ml-auto text-xs font-bold px-2.5 py-1 rounded bg-primary-600 text-white hover:bg-primary-700">{displayLanguage === 'nepali' ? 'सेभ' : 'Save'}</button>
                                <button onClick={() => setEditingReminderId(null)} className="text-xs font-medium px-2.5 py-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500">{displayLanguage === 'nepali' ? 'रद्द' : 'Cancel'}</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-gray-800 dark:text-gray-200 mt-0.5 line-clamp-2 flex-1">{item.note}</p>
                              <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => startEditReminder(item)} title={displayLanguage === 'nepali' ? 'सम्पादन' : 'Edit'} className="p-1 rounded text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => deleteReminder(item.id)} title={displayLanguage === 'nepali' ? 'हटाउनुहोस्' : 'Delete'} className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={enableGPS}
            title={userLocation ? 'Using your location' : 'Use my location'}
            aria-label={userLocation ? 'Using your location' : 'Use my location'}
            className={`p-1.5 rounded-lg transition-colors ${
              userLocation
                ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30'
                : 'text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {gpsLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <MapPin className="w-4 h-4" />
            }
          </button>
          <select
            value={displayLanguage}
            onChange={(e) => {
              const v = e.target.value as 'nepali' | 'english';
              setDisplayLanguage(v);
              try { localStorage.setItem('panchangaDisplayLanguage', v); } catch (err) {}
            }}
            className="input text-sm py-1 px-2 w-12"
            aria-label="Display language"
          >
            <option value="nepali">NP</option>
            <option value="english">EN</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="spinner mx-auto" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 devanagari">लोड हुँदैछ...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(() => {
            const isNep = displayLanguage === 'nepali';
            const engDateStr = englishDate
              ? `${englishDate.monthName || getMonthShortName(englishDate.month)} ${englishDate.day}, ${englishDate.year}`
              : '';
            const nepDateStr = nepaliDate
              ? (nepaliDate.monthName
                ? (isNep
                  ? `${nepaliDate.monthName} ${toNepaliNumerals(String(nepaliDate.day))}, ${toNepaliNumerals(String(nepaliDate.year))}`
                  : `${nepaliDate.monthName} ${nepaliDate.day}, ${nepaliDate.year}`)
                : `${nepaliDate.month}/${nepaliDate.day}/${nepaliDate.year}`)
              : '';
            const weekdayStr = isNep ? nepaliWeekday : englishWeekday;
            const now = new Date();
            const nowMin = now.getHours() * 60 + now.getMinutes();
            const srTime = panchanga?.sunrise || panchanga?.sunriseTime || todaySunrise;
            const srMin = srTime ? (() => { const [h,m] = srTime.split(':').map(Number); return h*60+m; })() : 330;
            const isBeforeSunrise = nowMin < srMin;
            const prevDayIdx = (now.getDay() + 6) % 7;
            const enDays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
            const npDays = ['आइतबार','सोमबार','मंगलबार','बुधबार','बिहिबार','शुक्रबार','शनिबार'];
            const astroWeekday = isBeforeSunrise ? (isNep ? npDays[prevDayIdx] : enDays[prevDayIdx]) : weekdayStr;
            return (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-dark-900/40 px-3 sm:px-4 py-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm sm:text-base font-bold text-primary-600 dark:text-primary-400">
                    {isNep ? nepDateStr : engDateStr}
                  </span>
                  {astroWeekday && <span className="text-xs text-gray-500 dark:text-gray-400">— {astroWeekday}</span>}
                  <span className="text-xs text-gray-400 dark:text-gray-500">|</span>
                  <span className={`text-sm sm:text-base font-bold text-primary-600 dark:text-primary-400 ${isNep ? '' : 'devanagari'}`}>
                    {isNep ? engDateStr : nepDateStr}
                  </span>
                  {isNep ? (englishWeekday && <span className="text-xs text-gray-500 dark:text-gray-400">— {englishWeekday}</span>)
                         : (nepaliWeekday && <span className="text-xs text-gray-500 dark:text-gray-400 devanagari">— {nepaliWeekday}</span>)}
                </div>
                {isBeforeSunrise && (
                  <div className="mt-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                    {isNep
                      ? `⚡ ज्योतिषीय दिन: ${npDays[prevDayIdx]} (सूर्योदय ${srTime || '05:16'} सम्म)`
                      : `⚡ Astrological day: ${enDays[prevDayIdx]} (until sunrise ${srTime || '05:16'})`}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Tab bar */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 -mx-1">
            {([
              { key: 'panchanga', label: displayLanguage === 'nepali' ? 'पंचांग' : 'Panchanga' },
              { key: 'muhurta', label: displayLanguage === 'nepali' ? 'शुभ साइत' : 'Subha Saait' },
              { key: 'chaughadi', label: displayLanguage === 'nepali' ? 'चौघडी' : 'Chaughadi' },
              { key: 'eclipse', label: displayLanguage === 'nepali' ? 'ग्रहण' : 'Eclipse' },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex-1 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'panchanga' && panchanga ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-dark-900/30 overflow-hidden">
              {/* Panchanga rows — each row: label left, value+time right, wraps on mobile */}
              {(() => {
                const valCls = displayLanguage === 'nepali' ? 'devanagari' : '';
                const isNep = displayLanguage === 'nepali';
                const fmtTime = (t: any) => {
                  if (!t) return '';
                  const s = stripTZ(t);
                  if (!isNep) return s;
                  // "03:30 PM, Jul 14" → "साँझ ०३:३० बजे सम्म"
                  // Nepali reads period-first: "[विहान/दिवा/साँझ/रात्री] [time] बजे सम्म".
                  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                  if (m) {
                    let hh = parseInt(m[1], 10);
                    const mm = parseInt(m[2], 10);
                    const ap = m[3] ? m[3].toUpperCase() : '';
                    if (ap === 'PM' && hh < 12) hh += 12;
                    if (ap === 'AM' && hh === 12) hh = 0;
                    const timeStr = toNepaliNumerals(`${m[1]}:${m[2]}`);
                    const period = ap ? `${nepPeriodWord(hh, mm)} ` : '';
                    return `${period}${timeStr} बजे सम्म`;
                  }
                  return toNepaliNumerals(s);
                };
                const getNext = (en: string | null, nep: string | null) => {
                  if (!en && !nep) return '';
                  return isNep ? (nep || en || '') : (en || nep || '');
                };
                // endDate is only present (from the backend) when the "until" time falls on a
                // different day than the one being viewed — same-day times don't get a date.
                const rows: { label: string; value: string; time?: string; endDate?: string; next?: string }[] = [];
                if (panchanga.thiti) rows.push({ label: isNep ? 'तिथि' : 'Tithi', value: extractPanchangaValueIntl(panchanga.thiti, displayLanguage), time: fmtTime(panchanga.thiti_time || panchanga.thitiTime), endDate: panchanga.thitiEndDate, next: getNext(panchanga.nextThiti, panchanga.nextThitiNep) });
                if (panchanga.nakshatra) rows.push({ label: isNep ? 'नक्षत्र' : 'Nakshatra', value: extractPanchangaValueIntl(panchanga.nakshatra, displayLanguage), time: fmtTime(panchanga.nakshatra_time || panchanga.nakshatraTime), endDate: panchanga.nakshatraEndDate, next: getNext(panchanga.nextNakshatra, panchanga.nextNakshatraNep) });
                if (panchanga.yoga) rows.push({ label: isNep ? 'योग' : 'Yoga', value: extractPanchangaValueIntl(panchanga.yoga, displayLanguage), time: fmtTime(panchanga.yoga_time || panchanga.yogaTime), endDate: panchanga.yogaEndDate, next: getNext(panchanga.nextYoga, panchanga.nextYogaNep) });
                if (panchanga.karana) rows.push({ label: isNep ? 'करण' : 'Karana', value: extractPanchangaValueIntl(panchanga.karana, displayLanguage), time: fmtTime(panchanga.karana_time || panchanga.karanaTime), endDate: panchanga.karanaEndDate, next: getNext(panchanga.nextKarana, panchanga.nextKaranaNep) });

                return (
                  <div>
                    {/* Header row */}
                    <div className={`flex items-center px-3 sm:px-4 py-2 bg-gray-50 dark:bg-gray-800/50 text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 ${isNep ? 'devanagari' : ''}`}>
                      <span className="w-24 shrink-0"></span>
                      <span className="flex-1 text-center">{isNep ? 'वर्तमान' : 'Current'}</span>
                      <span className="flex-1 text-right">{isNep ? 'अर्को' : 'Next'}</span>
                    </div>
                    {rows.map((r, i) => (
                      <div key={r.label} className={`flex items-start gap-1 px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-100 dark:border-gray-700/50`}>
                        <span className={`w-24 sm:w-28 shrink-0 text-sm sm:text-base font-bold text-gray-700 dark:text-gray-200 pt-0.5 leading-tight ${isNep ? 'devanagari' : ''}`}>{r.label}</span>
                        <div className="flex-1 text-center min-w-0">
                          <span className={`text-base sm:text-lg font-bold text-gray-900 dark:text-white ${valCls}`}>{r.value}</span>
                          {r.time && (
                            <div className="text-xs sm:text-sm font-semibold text-orange-700 dark:text-orange-400 mt-0.5">
                              {r.time}
                              {/* Nepali BS end-date, shown on every row so the "बजे सम्म" time is
                                  never ambiguous about which day it ends on. Nepali mode only —
                                  the backend label is a Devanagari BS month name. */}
                              {isNep && r.endDate && (
                                <span className="devanagari block text-[10px] sm:text-xs font-medium text-orange-700 dark:text-orange-300 mt-0.5">
                                  ({r.endDate} गते)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 text-right min-w-0">
                          {r.next && <span className={`text-sm sm:text-base font-semibold text-gray-800 dark:text-gray-100 ${valCls}`}>{r.next}</span>}
                        </div>
                      </div>
                    ))}

                    {/* Sunrise / Sunset side by side */}
                    {(panchanga.sunrise || panchanga.sunset) && (
                      <div className="border-t border-gray-100 dark:border-gray-700/50 px-3 sm:px-4 py-3">
                        <div className="grid grid-cols-2 gap-3">
                          {panchanga.sunrise && (
                            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2 sm:p-2.5 text-center">
                              <div className={`text-xs sm:text-sm font-bold text-orange-700 dark:text-orange-400 mb-0.5 ${isNep ? 'devanagari' : ''}`}>{displayLanguage === 'nepali' ? 'सूर्योदय' : 'Sunrise'}</div>
                              <div className={`text-sm sm:text-base font-bold text-gray-900 dark:text-white ${valCls}`}>{formatTimeWithPeriod(panchanga.sunrise || panchanga.sunriseTime || '', displayLanguage, true)}</div>
                            </div>
                          )}
                          {panchanga.sunset && (
                            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-2 sm:p-2.5 text-center">
                              <div className={`text-xs sm:text-sm font-bold text-indigo-600 dark:text-indigo-400 mb-0.5 ${isNep ? 'devanagari' : ''}`}>{displayLanguage === 'nepali' ? 'सूर्यास्त' : 'Sunset'}</div>
                              <div className={`text-sm sm:text-base font-bold text-gray-900 dark:text-white ${valCls}`}>{formatTimeWithPeriod(panchanga.sunset || panchanga.sunsetTime || '', displayLanguage, false)}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Chandra Rashi — 3-col with time and next */}
                    {(() => {
                      const cr = panchanga.chandra_rashi || panchanga.chandraRashi;
                      if (!cr) return null;
                      const hasRashiTime = !!(panchanga.chandra_time || panchanga.chandraTime) && String(panchanga.chandra_time || panchanga.chandraTime).trim();
                      const nextCr = hasRashiTime && panchanga.nextChandraRashi ? localizeRashi(panchanga.nextChandraRashi, displayLanguage) : '';
                      return (
                        <div className="border-t border-gray-100 dark:border-gray-700/50 flex items-start gap-1 px-3 sm:px-4 py-2 sm:py-2.5">
                          <span className={`w-24 sm:w-28 shrink-0 text-sm sm:text-base font-bold text-gray-700 dark:text-gray-200 pt-0.5 leading-tight ${isNep ? 'devanagari' : ''}`}>{isNep ? 'चन्द्र राशि' : 'Chandra Rashi'}</span>
                          <div className="flex-1 text-center min-w-0">
                            <span className={`text-base sm:text-lg font-bold text-gray-900 dark:text-white ${valCls}`}>{localizeRashi(cr, displayLanguage)}</span>
                            {hasRashiTime && <div className="text-xs sm:text-sm font-semibold text-orange-700 dark:text-orange-400 mt-0.5">{isNep ? fmtTime(panchanga.chandra_time || panchanga.chandraTime) : `till ${fmtTime(panchanga.chandra_time || panchanga.chandraTime)}`}</div>}
                          </div>
                          <div className="flex-1 text-right min-w-0">
                            {nextCr && <span className={`text-sm sm:text-base font-semibold text-gray-800 dark:text-gray-100 ${valCls}`}>{nextCr}</span>}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Compact 2-col grid for single-value fields */}
                    {(() => {
                      const lunarMonthNep: Record<string, string> = { 'Chaitra':'चैत्र', 'Vaishakha':'वैशाख', 'Jyeshtha':'ज्येष्ठ', 'Ashadha':'आषाढ', 'Shravana':'श्रावण', 'Bhadrapada':'भाद्रपद', 'Ashwin':'आश्विन', 'Kartik':'कार्तिक', 'Margashirsha':'मार्गशीर्ष', 'Pausha':'पौष', 'Magha':'माघ', 'Phalguna':'फाल्गुन' };
                      const rituNep: Record<string, string> = { 'Vasanta':'वसन्त', 'Grishma':'ग्रीष्म', 'Varsha':'वर्षा', 'Sharad':'शरद', 'Hemanta':'हेमन्त', 'Shishira':'शिशिर' };
                      const samvatsaraNep: Record<string, string> = { 'Prabhava':'प्रभव', 'Vibhava':'विभव', 'Shukla':'शुक्ल', 'Pramodoota':'प्रमोदूत', 'Prajothpatti':'प्रजोत्पत्ति', 'Angirasa':'आंगिरस', 'Shrimukha':'श्रीमुख', 'Bhava':'भाव', 'Yuva':'युवा', 'Dhata':'धाता', 'Ishwara':'ईश्वर', 'Bahudhanya':'बहुधान्य', 'Pramathi':'प्रमाथी', 'Vikrama':'विक्रम', 'Vrisha':'वृष', 'Chitrabhanu':'चित्रभानु', 'Svabhanu':'स्वभानु', 'Tarana':'तारण', 'Parthiva':'पार्थिव', 'Vyaya':'व्यय', 'Sarvajith':'सर्वजित्', 'Sarvadhari':'सर्वधारी', 'Virodhi':'विरोधी', 'Vikruti':'विकृति', 'Khara':'खर', 'Nandana':'नन्दन', 'Vijaya':'विजय', 'Jaya':'जय', 'Manmatha':'मन्मथ', 'Durmukhi':'दुर्मुखी', 'Hevilambi':'हेविलम्बी', 'Vilambi':'विलम्बी', 'Vikari':'विकारी', 'Sharvari':'शार्वरी', 'Plava':'प्लव', 'Shubhakruth':'शुभकृत्', 'Shobhakruth':'शोभकृत्', 'Krodhi':'क्रोधी', 'Vishvavasu':'विश्वावसु', 'Parabhava':'पराभव', 'Plavanga':'प्लवंग', 'Keelaka':'कीलक', 'Saumya':'सौम्य', 'Sadharana':'साधारण', 'Virodhikruth':'विरोधीकृत्', 'Pareedhavi':'परिधावी', 'Pramadeecha':'प्रमादीच', 'Aananda':'आनन्द', 'Rakshasa':'राक्षस', 'Nala':'नल', 'Pingala':'पिंगल', 'Kalayukthi':'कालयुक्ति', 'Siddharthi':'सिद्धार्थी', 'Raudra':'रौद्र', 'Raudri':'रौद्री', 'Durmathi':'दुर्मति', 'Dundubhi':'दुन्दुभी', 'Rudhirodgari':'रुधिरोद्गारी', 'Raktakshi':'रक्ताक्षी', 'Krodhana':'क्रोधन', 'Akshaya':'अक्षय' };
                      const cells: { label: string; value: string; color?: string }[] = [];

                      const sr2 = panchanga.surya_rashi || panchanga.suryaRashi;
                      if (sr2) cells.push({ label: isNep ? 'सूर्य राशि' : 'Surya Rashi', value: localizeRashi(sr2, displayLanguage) });
                      const gr = panchanga.guru_rashi || panchanga.guruRashi;
                      const grVal = gr ? localizeRashi(gr, displayLanguage) : '';
                      if (grVal) cells.push({ label: isNep ? 'गुरु राशि' : 'Guru Rashi', value: grVal });
                      const paksha = panchanga.pakshya || panchanga.paksha;
                      if (paksha) cells.push({ label: isNep ? 'पक्ष' : 'Paksha', value: isNep ? (String(paksha).includes('Shukla') ? 'शुक्ल पक्ष' : String(paksha).includes('Krishna') ? 'कृष्ण पक्ष' : String(paksha)) : String(paksha) });
                      const lm = panchanga.lunarMonth || panchanga.lunar_month;
                      if (lm) cells.push({ label: isNep ? 'चान्द्र मास' : 'Lunar Month', value: isNep ? (lunarMonthNep[String(lm)] || String(lm)) : String(lm) });
                      const ritu = panchanga.ritu;
                      if (ritu) cells.push({ label: isNep ? 'ऋतु' : 'Ritu', value: isNep ? (rituNep[String(ritu)] || String(ritu)) : String(ritu) });
                      const aayan = panchanga.aayan;
                      if (aayan) cells.push({ label: isNep ? 'आयन' : 'Ayana', value: isNep ? (String(aayan).includes('uttara') ? 'उत्तरायन' : String(aayan).includes('dakshina') ? 'दक्षिणायन' : String(aayan)) : String(aayan).charAt(0).toUpperCase() + String(aayan).slice(1) });
                      const sv = panchanga.samvatsara;
                      if (sv) cells.push({ label: isNep ? 'संवत्सर' : 'Samvatsara', value: isNep ? (samvatsaraNep[String(sv)] || String(sv)) : String(sv) });
                      const ns = panchanga.nepal_sambat || panchanga.nepalSambat;
                      if (ns) cells.push({ label: isNep ? 'नेपाल संवत' : 'Nepal Sambat', value: isNep ? toNepaliNumerals(String(ns)) : String(ns) });
                      const rahu = panchanga.rahu_kaal || panchanga.rahuKaal;
                      if (rahu) cells.push({ label: isNep ? 'राहु काल' : 'Rahu Kaal', value: formatRahuKaalForDisplay(rahu, displayLanguage), color: 'text-red-600 dark:text-red-400' });

                      if (cells.length === 0) return null;
                      return (
                        <div className="border-t border-gray-100 dark:border-gray-700/50 grid grid-cols-2 gap-px bg-gray-100 dark:bg-gray-700/50">
                          {cells.map((c) => (
                            <div key={c.label} className="bg-white dark:bg-dark-900/30 px-2 sm:px-3 py-2 sm:py-2.5 flex items-center justify-between gap-2">
                              <span className={`text-xs sm:text-sm font-semibold shrink-0 leading-tight ${isNep ? 'devanagari' : ''} ${c.color || 'text-gray-600 dark:text-gray-300'}`}>{c.label}</span>
                              <span className={`text-sm sm:text-base font-bold text-gray-900 dark:text-white text-right leading-tight ${valCls}`}>{c.value}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Bisesh Parva */}
                    {(panchanga.bisesh_parva || panchanga.biseshParva) && (
                      <div className="border-t border-gray-100 dark:border-gray-700/50 flex justify-between items-start gap-2 px-3 sm:px-4 py-2.5">
                        <span className={`text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider pt-0.5 shrink-0 ${isNep ? 'devanagari' : ''}`}>{isNep ? 'विशेष पर्व' : 'Bisesh Parva'}</span>
                        <span className={`text-sm font-bold text-gray-900 dark:text-white text-right ${valCls}`}>{localizeFestival(panchanga.bisesh_parva || panchanga.biseshParva, displayLanguage)}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : activeTab === 'panchanga' ? (
            <div className="text-center py-4 px-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-xs sm:text-sm devanagari font-medium text-amber-700 dark:text-amber-300">पञ्चाङ्ग डाटा उपलब्ध छैन</p>
            </div>
          ) : null}

          {activeTab === 'muhurta' && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-dark-900/30 overflow-hidden">
              {(() => {
                const isNep = displayLanguage === 'nepali';
                const saait = panchanga?.shubha_saait || panchanga?.shubhaSaait;
                const rahu = panchanga?.rahu_kaal || panchanga?.rahuKaal;
                const sr = panchanga?.sunrise || panchanga?.sunriseTime;
                const ss = panchanga?.sunset || panchanga?.sunsetTime;
                const toMin = (s: string) => { const [h,m] = s.split(':').map(Number); return h*60+m; };
                const nn = (s: string) => isNep ? s.replace(/[0-9]/g, d => '०१२३४५६७८९'[Number(d)]) : s;
                const abhijit = sr && ss ? (() => {
                  const mid = Math.round((toMin(sr) + toMin(ss)) / 2);
                  return `${fmtMin(mid - 24)} - ${fmtMin(mid + 24)}`;
                })() : null;

                // Day chaughadi windows for the selected date
                const nowD = new Date();
                const selDateStr = panchanga?.date || (englishDate ? `${englishDate.year}-${pad(englishDate.month)}-${pad(englishDate.day)}` : '');
                const selDate = selDateStr ? new Date(selDateStr + 'T12:00:00') : nowD;
                const dayCh = (sr && ss) ? computeDayChaughadi(toMin(sr), toMin(ss), selDate.getDay()) : [];
                const goodCh = dayCh.filter(p => p.type === 'good');
                const badCh = dayCh.filter(p => p.type === 'bad');

                const ceremonyInfo: Record<string, { keys: string[]; backend: string; np: string; en: string; griha?: boolean }> = {
                  agni:         { keys: ['agni'],                          backend: 'agni',           np: 'अग्नि',        en: 'Agni' },
                  rudri:        { keys: ['rudri'],                         backend: 'rudri',          np: 'रुद्री',       en: 'Rudri' },
                  annaprashan:  { keys: ['annaprassan', 'annaprashan'],    backend: 'annaprashan',    np: 'अन्नप्राशन',   en: 'Annaprashan' },
                  grihaNirman:  { keys: ['griha_rambha', 'griha_nirman'],  backend: 'griha_aarambha', np: 'गृह निर्माण',  en: 'Griha Nirman', griha: true },
                  grihaPrabesh: { keys: ['griha_brabesh', 'grihapravesh'], backend: 'griha_prabesh',  np: 'गृह प्रवेश',   en: 'Griha Prabesh', griha: true },
                };

                const subTabs: { key: typeof muhurtaSubTab; label: string; badge?: number }[] = [
                  { key: 'shubha',       label: isNep ? 'शुभ बेला' : 'Shubha Bela' },
                  { key: 'ashubha',      label: isNep ? 'अशुभ बेला' : 'Ashubha Bela' },
                  { key: 'agni',         label: isNep ? 'अग्नि' : 'Agni' },
                  { key: 'rudri',        label: isNep ? 'रुद्री' : 'Rudri' },
                  { key: 'annaprashan',  label: isNep ? 'अन्नप्राशन' : 'Annaprashan' },
                  { key: 'grihaNirman',  label: isNep ? 'गृह निर्माण' : 'Griha Nirman' },
                  { key: 'grihaPrabesh', label: isNep ? 'गृह प्रवेश' : 'Griha Prabesh' },
                  { key: 'asta',         label: isNep ? 'अस्त' : 'Asta', badge: planetAstaRows.length || undefined },
                ];

                const windowRow = (label: string, time: string, good: boolean, key: string) => (
                  <div key={key} className={`flex justify-between items-center gap-2 px-2.5 py-2 rounded ${good ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                    <span className={`text-sm font-bold ${good ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'} ${isNep ? 'devanagari' : ''}`}>{label}</span>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{nn(time)}</span>
                  </div>
                );

                // Real muhurtas computed by the backend (Brahma, Abhijit, etc.)
                const muh = (panchanga?.muhurtas || { shubha: [], ashubha: [] }) as { shubha: any[]; ashubha: any[] };
                const muhIcon: Record<string, string> = {
                  brahma: '🌞', abhijit: '🌅', vijaya: '🔵', godhuli: '🌆', nishita: '🌙', amrit_kalam: '🟢',
                  rahu: '🔴', gulika: '⚫', yamaganda: '🟠', dur_muhurta: '🔴', varjyam: '⚫',
                };
                const qualityLabel: Record<string, { np: string; en: string; cls: string }> = {
                  excellent: { np: 'उत्कृष्ट', en: 'Excellent', cls: 'bg-green-200 dark:bg-green-800/50 text-green-800 dark:text-green-200' },
                  very_good: { np: 'धेरै राम्रो', en: 'Very Good', cls: 'bg-emerald-200 dark:bg-emerald-800/50 text-emerald-800 dark:text-emerald-200' },
                  good: { np: 'राम्रो', en: 'Good', cls: 'bg-teal-200 dark:bg-teal-800/50 text-teal-800 dark:text-teal-200' },
                  avoid: { np: 'त्याग्नुहोस्', en: 'Avoid', cls: 'bg-red-200 dark:bg-red-800/50 text-red-800 dark:text-red-200' },
                };
                const muhurtaRow = (item: any, good: boolean, key: string) => {
                  const q = qualityLabel[item.quality] || qualityLabel[good ? 'good' : 'avoid'];
                  return (
                    <div key={key} className={`flex items-center gap-2 px-2.5 py-2 rounded ${good ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                      <span className="shrink-0">{muhIcon[item.key] || (good ? '🟢' : '🔴')}</span>
                      <span className={`text-sm font-bold flex-1 min-w-0 ${good ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'} ${isNep ? 'devanagari' : ''}`}>{isNep ? item.name_nep : item.name}</span>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 shrink-0">{nn(`${item.start}–${item.end}`)}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${q.cls} ${isNep ? 'devanagari' : ''}`}>{isNep ? q.np : q.en}</span>
                    </div>
                  );
                };

                return (
                  <div>
                    {/* Sub-tab grid (3 per row, 2 rows) */}
                    <div className="grid grid-cols-3 gap-px bg-gray-200 dark:bg-gray-700">
                      {subTabs.map((t) => (
                        <button
                          key={t.key}
                          onClick={() => setMuhurtaSubTab(t.key)}
                          className={`relative py-2 px-1 text-[11px] sm:text-xs font-bold transition-colors ${
                            muhurtaSubTab === t.key
                              ? 'bg-primary-600 text-white'
                              : 'bg-white dark:bg-dark-900/30 text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                          } ${isNep ? 'devanagari' : ''}`}
                        >
                          {t.label}
                          {t.badge ? (
                            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full bg-orange-500 text-white">{t.badge}</span>
                          ) : null}
                        </button>
                      ))}
                    </div>

                    <div className="p-3 space-y-2">
                      {/* शुभ बेला */}
                      {muhurtaSubTab === 'shubha' && (
                        (muh.shubha.length > 0 || abhijit || goodCh.length > 0) ? (
                          <>
                            {muh.shubha.length > 0
                              ? muh.shubha.map((m, i) => muhurtaRow(m, true, `sm${i}`))
                              : (
                                <>
                                  {abhijit && windowRow(isNep ? 'अभिजित मुहूर्त' : 'Abhijit Muhurta', abhijit, true, 'abhijit')}
                                  {goodCh.map((p, i) => windowRow(isNep ? p.np : p.en, `${p.start} - ${p.end}`, true, `g${i}`))}
                                </>
                              )}
                            {saait && localizeShubhaSaait(saait, displayLanguage) && (
                              <div className="pt-2 border-t border-gray-100 dark:border-gray-700/50">
                                <div className={`text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1 ${isNep ? 'devanagari' : ''}`}>
                                  {isNep ? 'आजका शुभ साइत' : "Today's Shubha Saait"}
                                </div>
                                <p className={`text-sm font-semibold text-gray-800 dark:text-gray-100 ${isNep ? 'devanagari' : ''}`}>
                                  {localizeShubhaSaait(saait, displayLanguage)}
                                </p>
                              </div>
                            )}
                            <p className={`text-[10px] text-gray-400 dark:text-gray-500 pt-1 ${isNep ? 'devanagari' : ''}`}>
                              {isNep ? 'यी समयमा शुभ कार्य गर्न उपयुक्त मानिन्छ।' : 'These windows are considered suitable for auspicious work.'}
                            </p>
                          </>
                        ) : (
                          <p className={`text-sm text-gray-500 dark:text-gray-400 text-center py-4 ${isNep ? 'devanagari' : ''}`}>{isNep ? 'शुभ बेला उपलब्ध छैन' : 'No Shubha Bela available'}</p>
                        )
                      )}

                      {/* अशुभ बेला */}
                      {muhurtaSubTab === 'ashubha' && (
                        (muh.ashubha.length > 0 || rahu || badCh.length > 0) ? (
                          <>
                            {muh.ashubha.length > 0
                              ? muh.ashubha.map((m, i) => muhurtaRow(m, false, `am${i}`))
                              : (
                                <>
                                  {rahu && windowRow(isNep ? 'राहु काल' : 'Rahu Kaal', formatRahuKaalForDisplay(rahu, displayLanguage), false, 'rahu')}
                                  {badCh.map((p, i) => windowRow(isNep ? p.np : p.en, `${p.start} - ${p.end}`, false, `b${i}`))}
                                </>
                              )}
                            <p className={`text-[10px] text-gray-400 dark:text-gray-500 pt-1 ${isNep ? 'devanagari' : ''}`}>
                              {isNep ? 'यी समयमा शुभ कार्य नगर्न सल्लाह दिइन्छ।' : 'Avoid starting auspicious work during these windows.'}
                            </p>
                          </>
                        ) : (
                          <p className={`text-sm text-gray-500 dark:text-gray-400 text-center py-4 ${isNep ? 'devanagari' : ''}`}>{isNep ? 'अशुभ बेला उपलब्ध छैन' : 'No Ashubha Bela available'}</p>
                        )
                      )}

                      {/* Ceremony tabs — auspicious dates for the viewed month */}
                      {ceremonyInfo[muhurtaSubTab] && (() => {
                        const info = ceremonyInfo[muhurtaSubTab];
                        const isGriha = !!info.griha;
                        const nepMonthNames = ['बैशाख','जेष्ठ','आषाढ','श्रावण','भाद्र','आश्विन','कार्तिक','मङ्सिर','पौष','माघ','फाल्गुन','चैत्र'];
                        const enMonthNames = ['Baishakh','Jestha','Ashadh','Shrawan','Bhadra','Ashwin','Kartik','Mangsir','Poush','Magh','Falgun','Chaitra'];
                        const curMonth = Number((nepaliDate as any)?.month) || 0;
                        const monthLabel = curMonth ? (isNep ? nepMonthNames[curMonth - 1] : enMonthNames[curMonth - 1]) : '';
                        const todayIso = (nepaliDate as any)?.year ? `${(nepaliDate as any).year}-${pad((nepaliDate as any).month)}-${pad((nepaliDate as any).day)}` : '';
                        // Entries for this ceremony in the viewed month, sorted by day
                        const entries = ceremonyMuhurtas
                          .filter((c) => c.ceremony === info.backend)
                          .sort((a, b) => (a.nepali_full_date || '').localeCompare(b.nepali_full_date || ''));

                        // For Agni/Rudri: dates that have the OTHER ceremony too → highlight
                        const pairKey = muhurtaSubTab === 'agni' ? 'rudri' : muhurtaSubTab === 'rudri' ? 'agni' : null;
                        const pairedDates = new Set<string>();
                        if (pairKey) {
                          ceremonyMuhurtas
                            .filter((c) => c.ceremony === pairKey)
                            .forEach((c) => pairedDates.add(c.nepali_full_date));
                        }
                        const pairLabel = pairKey === 'agni' ? (isNep ? 'अग्नि' : 'Agni') : pairKey === 'rudri' ? (isNep ? 'रुद्री' : 'Rudri') : '';

                        return (
                          <div className="space-y-2">
                            <div className={`flex items-center justify-between text-sm font-bold text-gray-700 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>
                              <span>{(isNep ? info.np : info.en)}{monthLabel ? ` — ${monthLabel}` : ''}</span>
                              {pairKey && (
                                <span className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                                  ★ = {isNep ? `${info.np}+${pairLabel} दुवै` : `both ${info.en} + ${pairLabel}`}
                                </span>
                              )}
                            </div>

                            {entries.length === 0 ? (
                              <div className={`text-sm text-gray-500 dark:text-gray-400 text-center py-4 rounded-lg bg-gray-50 dark:bg-gray-800/40 ${isNep ? 'devanagari' : ''}`}>
                                {isNep ? 'यस महिनामा साइत छैन' : 'No auspicious dates this month'}
                              </div>
                            ) : isGriha ? (
                              // Griha ceremonies keep the fuller layout (lagna + good time matter)
                              <div className="space-y-1.5">
                                {entries.map((e) => {
                                  const day = Number((e.nepali_full_date || '').split('-')[2]) || 0;
                                  const isToday = e.nepali_full_date === todayIso;
                                  return (
                                    <div key={e.id} className={`rounded-lg border px-3 py-2 ${isToday ? 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-dark-900/30'}`}>
                                      <div className="flex items-center justify-between gap-2">
                                        <span className={`text-sm font-bold text-green-700 dark:text-green-300 ${isNep ? 'devanagari' : ''}`}>
                                          ✅ {monthLabel} {nn(String(day))}{isToday ? (isNep ? ' (आज)' : ' (today)') : ''}
                                        </span>
                                        {e.eng_date && <span className="text-[10px] text-gray-400">{e.eng_date}</span>}
                                      </div>
                                      {(e.lagna || e.good_time) && (
                                        <div className="mt-1 space-y-0.5">
                                          {e.lagna && (
                                            <div className="flex justify-between gap-2">
                                              <span className={`text-xs text-gray-500 dark:text-gray-400 ${isNep ? 'devanagari' : ''}`}>{isNep ? 'लग्न' : 'Lagna'}</span>
                                              <span className={`text-xs font-semibold text-gray-800 dark:text-gray-100 ${isNep ? 'devanagari' : ''}`}>{e.lagna}</span>
                                            </div>
                                          )}
                                          {e.good_time && (
                                            <div className="flex justify-between gap-2">
                                              <span className={`text-xs text-gray-500 dark:text-gray-400 ${isNep ? 'devanagari' : ''}`}>{isNep ? 'शुभ समय' : 'Good Time'}</span>
                                              <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">{nn(String(e.good_time))}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {e.note && <p className={`text-xs italic text-gray-500 dark:text-gray-400 mt-0.5 ${isNep ? 'devanagari' : ''}`}>{e.note}</p>}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              // Non-griha (Agni / Rudri / Annaprashan): compact day chips
                              <div className="flex flex-wrap gap-1.5">
                                {entries.map((e) => {
                                  const day = Number((e.nepali_full_date || '').split('-')[2]) || 0;
                                  const isToday = e.nepali_full_date === todayIso;
                                  const isPaired = pairedDates.has(e.nepali_full_date);
                                  const chipCls = isPaired
                                    ? 'border-purple-400 dark:border-purple-500 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-100'
                                    : 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
                                  const ring = isToday ? 'ring-2 ring-offset-1 ring-green-400 dark:ring-green-500 dark:ring-offset-dark-900' : '';
                                  return (
                                    <span
                                      key={e.id}
                                      title={`${monthLabel} ${day}${e.eng_date ? ` (${e.eng_date})` : ''}${isPaired ? ` — ${pairLabel} also` : ''}${e.note ? ` — ${e.note}` : ''}`}
                                      className={`inline-flex items-center gap-1 min-w-[2.25rem] justify-center px-2 py-1 rounded-md border text-sm font-bold ${chipCls} ${ring} ${isNep ? 'devanagari' : ''}`}
                                    >
                                      {isPaired && <span className="text-[11px] leading-none">★</span>}
                                      <span>{nn(String(day))}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Asta (Planet Combustion) sub-tab */}
                      {muhurtaSubTab === 'asta' && (() => {
                        const planetLabels: Record<string, { np: string; en: string }> = {
                          guru:    { np: 'गुरु (बृहस्पति)', en: 'Guru (Jupiter)' },
                          shukra:  { np: 'शुक्र',           en: 'Shukra (Venus)' },
                          mangal:  { np: 'मंगल',            en: 'Mangal (Mars)' },
                          budha:   { np: 'बुध',             en: 'Budha (Mercury)' },
                          shani:   { np: 'शनि',             en: 'Shani (Saturn)' },
                          chandra: { np: 'चन्द्र',          en: 'Chandra (Moon)' },
                        };
                        return (
                          <div className="space-y-2">
                            <div className={`text-sm font-bold text-gray-700 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>
                              {isNep ? 'ग्रह अस्त' : 'Planet Combust'}
                              <span className="text-[10px] font-normal text-gray-500 ml-2">
                                {isNep ? '(यस मितिमा सक्रिय)' : '(active on selected date)'}
                              </span>
                            </div>
                            {planetAstaRows.length === 0 ? (
                              <div className={`text-sm text-gray-500 dark:text-gray-400 text-center py-4 rounded-lg bg-gray-50 dark:bg-gray-800/40 ${isNep ? 'devanagari' : ''}`}>
                                {isNep ? 'यस मितिमा कुनै ग्रह अस्त छैन' : 'No planet is combust on this date'}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {planetAstaRows.map((row) => {
                                  const keys = String(row.planets || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                                  return (
                                    <div key={row.id} className="rounded-lg border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 px-3 py-2">
                                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                                        {keys.map((k: string) => (
                                          <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-bold bg-orange-200 dark:bg-orange-800/60 text-orange-800 dark:text-orange-100 ${isNep ? 'devanagari' : ''}`}>
                                            🔥 {isNep ? (planetLabels[k]?.np || k) : (planetLabels[k]?.en || k)} {isNep ? 'अस्त' : 'Asta'}
                                          </span>
                                        ))}
                                      </div>
                                      <div className={`flex justify-between items-center text-xs text-gray-600 dark:text-gray-300 ${isNep ? 'devanagari' : ''}`}>
                                        <span>{isNep ? 'देखि' : 'From'}: <span className="font-semibold">{row.start_nepali_date}</span></span>
                                        <span>{isNep ? 'सम्म' : 'Until'}: <span className="font-semibold">{row.end_nepali_date}</span></span>
                                      </div>
                                      {(row.start_eng_date || row.end_eng_date) && (
                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                          {row.start_eng_date} → {row.end_eng_date}
                                        </div>
                                      )}
                                      {row.note && <p className={`text-xs italic text-gray-500 dark:text-gray-400 mt-1 ${isNep ? 'devanagari' : ''}`}>{row.note}</p>}
                                    </div>
                                  );
                                })}
                                <p className={`text-[10px] text-gray-400 dark:text-gray-500 pt-1 ${isNep ? 'devanagari' : ''}`}>
                                  {isNep ? 'अस्तको समयमा शुभ कार्य वर्जित मानिन्छ।' : 'Auspicious work is discouraged during a planet\'s combust period.'}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* Festivals & Events — embedded inside Subha Saait tab */}
              <div className="border-t border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-3">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-accent-500" />
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white">{displayLanguage === 'nepali' ? 'चाडपर्व र कार्यक्रम' : 'Festivals & Events'}</h4>
                </div>
                <EventsSidebar embedded />
              </div>
            </div>
          )}

          {activeTab === 'chaughadi' && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-dark-900/30 overflow-hidden">
              {(() => {
                const isNep = displayLanguage === 'nepali';
                const sr = panchanga?.sunrise || panchanga?.sunriseTime;
                const ss = panchanga?.sunset || panchanga?.sunsetTime;
                if (!sr || !ss) return <p className="text-sm text-gray-500 text-center py-4">{isNep ? 'चौघडी डाटा उपलब्ध छैन' : 'No Chaughadi data'}</p>;

                const toMin = (s: string) => { const [h,m] = s.split(':').map(Number); return h*60+m; };
                const pad2 = (n: number) => String(n).padStart(2,'0');
                const fmt = (m: number) => { const mm = ((m % (24*60)) + 24*60) % (24*60); return `${pad2(Math.floor(mm/60))}:${pad2(mm%60)}`; };
                const srMin = toMin(sr);
                const ssMin = toMin(ss);
                const now = new Date();
                const nowMin = now.getHours() * 60 + now.getMinutes();
                // Determine selected date's weekday
                const selDateStr = panchanga?.date || (englishDate ? `${englishDate.year}-${String(englishDate.month).padStart(2,'0')}-${String(englishDate.day).padStart(2,'0')}` : '');
                const selDate = selDateStr ? new Date(selDateStr + 'T12:00:00') : now;
                const isToday = selDate.toDateString() === now.toDateString();
                const isNight = isToday ? (nowMin >= ssMin || nowMin < srMin) : false;

                const C = CHAUGHADI_NAMES;
                const daySeq = DAY_SEQ;
                const nightSeq = NIGHT_SEQ;

                const calendarDay = selDate.getDay();
                const beforeSunrise = isToday && nowMin < srMin;
                const dayOfWeek = beforeSunrise ? (calendarDay + 6) % 7 : calendarDay;

                const enDays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                const npDays = ['आइतबार','सोमबार','मंगलबार','बुधबार','बिहिबार','शुक्रबार','शनिबार'];
                const varaLabel = isNep
                  ? `${npDays[dayOfWeek]} चौघडी${beforeSunrise ? ' (सूर्योदय सम्म)' : ''}`
                  : `${enDays[dayOfWeek]} Chaughadi${beforeSunrise ? ' (until sunrise)' : ''}`;

                const dayPartLen = (ssMin - srMin) / 8;
                const nightPartLen = (24*60 - ssMin + srMin) / 8;

                const dayPeriods = daySeq[dayOfWeek].map((name, i) => {
                  const startM = Math.round(srMin + i * dayPartLen);
                  const endM = Math.round(srMin + (i+1) * dayPartLen);
                  const isCurrent = isToday && !isNight && nowMin >= startM && nowMin < endM;
                  return { ...C[name], start: fmt(startM), end: fmt(endM), startM, endM, isCurrent };
                });
                const nightPeriods = nightSeq[dayOfWeek].map((name, i) => {
                  const rawStart = Math.round(ssMin + i * nightPartLen);
                  const rawEnd = Math.round(ssMin + (i+1) * nightPartLen);
                  const startM = rawStart % (24*60);
                  const endM = rawEnd % (24*60);
                  let isCurrent = false;
                  if (isToday && isNight) {
                    if (startM > endM) isCurrent = nowMin >= startM || nowMin < endM;
                    else isCurrent = nowMin >= startM && nowMin < endM;
                  }
                  return { ...C[name], start: fmt(startM), end: fmt(endM), startM, endM, isCurrent };
                });

                const currentDayIdx = dayPeriods.findIndex(p => p.isCurrent);
                const currentNightIdx = nightPeriods.findIndex(p => p.isCurrent);
                const autoTab = isNight ? 'night' : 'day';

                return <ChaughadiPanel isNep={isNep} dayPeriods={dayPeriods} nightPeriods={nightPeriods} autoTab={autoTab} currentDayIdx={currentDayIdx} currentNightIdx={currentNightIdx} varaLabel={varaLabel} />;
              })()}
            </div>
          )}

          {activeTab === 'eclipse' && (() => {
            const isNep = displayLanguage === 'nepali';
            const nepNum = (s: string) => s.replace(/[0-9]/g, d => '०१२३४५६७८९'[Number(d)]);

            const eclipses = [
              {
                date: '2026-02-17', type: 'annular_solar',
                en: { name: 'Annular Solar Eclipse', date: 'February 17, 2026', time: '19:45 - 01:00 NPT', maxTime: '22:28 NPT', duration: '5h 15m', rashi: 'Kumbha (Aquarius)', nakshatra: 'Shatabhisha', visibility: 'Antarctica, South America, Africa', sutak: '12 hours before', sparsha: 'Not visible from Nepal', madhya: 'Not visible from Nepal', moksha: 'Not visible from Nepal' },
                np: { name: 'कंकणाकृति सूर्यग्रहण', date: 'फाल्गुन ५, २०८२', time: '१९:४५ - ०१:०० NPT', maxTime: '२२:२८ NPT', duration: '५ घण्टा १५ मि.', rashi: 'कुम्भ', nakshatra: 'शतभिषा', visibility: 'अन्टार्कटिका, दक्षिण अमेरिका, अफ्रिका', sutak: '१२ घण्टा अगाडि', sparsha: 'नेपालबाट अदृश्य', madhya: 'नेपालबाट अदृश्य', moksha: 'नेपालबाट अदृश्य' },
                favorable: ['Mesha','Mithuna','Simha','Tula','Dhanu'], favorableNp: ['मेष','मिथुन','सिंह','तुला','धनु'],
                unfavorable: ['Kumbha','Karka','Vrishchika'], unfavorableNp: ['कुम्भ','कर्कट','वृश्चिक'],
                nepVisible: false,
              },
              {
                date: '2026-03-03', type: 'total_lunar',
                en: { name: 'Total Lunar Eclipse', date: 'March 3, 2026', time: '14:21 - 19:44 NPT', maxTime: '17:03 NPT', duration: '5h 23m', rashi: 'Simha (Leo)', nakshatra: 'Purva Phalguni', visibility: 'Asia, Australia, Pacific, Americas', sutak: '9 hours before', sparsha: 'Before moonrise — not seen', madhya: '06:06 PM NPT', moksha: '08:08 PM NPT' },
                np: { name: 'पूर्ण चन्द्रग्रहण', date: 'फाल्गुन १९, २०८२', time: '१४:२१ - १९:४४ NPT', maxTime: '१७:०३ NPT', duration: '५ घण्टा २३ मि.', rashi: 'सिंह', nakshatra: 'पूर्व फाल्गुनी', visibility: 'एसिया, अस्ट्रेलिया, प्रशान्त, अमेरिका', sutak: '९ घण्टा अगाडि', sparsha: 'चन्द्रोदय अघि — अदृश्य', madhya: '०६:०६ बेलुका NPT', moksha: '०८:०८ बेलुका NPT' },
                favorable: ['Mesha','Mithuna','Tula','Dhanu','Kumbha'], favorableNp: ['मेष','मिथुन','तुला','धनु','कुम्भ'],
                unfavorable: ['Simha','Vrishabha','Vrishchika'], unfavorableNp: ['सिंह','वृषभ','वृश्चिक'],
                nepVisible: true,
              },
              {
                date: '2026-08-12', type: 'total_solar',
                en: { name: 'Total Solar Eclipse', date: 'August 12, 2026', time: '04:14 - 09:20 NPT', maxTime: '06:46 NPT', duration: '5h 06m', rashi: 'Karka (Cancer)', nakshatra: 'Ashlesha', visibility: 'Arctic, Europe, N. Africa, N. Asia', sutak: '12 hours before', sparsha: 'Not visible from Nepal', madhya: 'Not visible from Nepal', moksha: 'Not visible from Nepal' },
                np: { name: 'पूर्ण सूर्यग्रहण', date: 'श्रावण २८, २०८३', time: '०४:१४ - ०९:२० NPT', maxTime: '०६:४६ NPT', duration: '५ घण्टा ६ मि.', rashi: 'कर्कट', nakshatra: 'आश्लेषा', visibility: 'आर्कटिक, युरोप, उत्तरी अफ्रिका, उत्तरी एसिया', sutak: '१२ घण्टा अगाडि', sparsha: 'नेपालबाट अदृश्य', madhya: 'नेपालबाट अदृश्य', moksha: 'नेपालबाट अदृश्य' },
                favorable: ['Vrishabha','Kanya','Vrishchika','Meena'], favorableNp: ['वृषभ','कन्या','वृश्चिक','मीन'],
                unfavorable: ['Karka','Mesha','Tula'], unfavorableNp: ['कर्कट','मेष','तुला'],
                nepVisible: false,
              },
              {
                date: '2026-08-28', type: 'partial_lunar',
                en: { name: 'Partial Lunar Eclipse', date: 'August 28, 2026', time: '03:51 - 08:36 NPT', maxTime: '06:13 NPT', duration: '4h 45m', rashi: 'Kumbha (Aquarius)', nakshatra: 'Purva Bhadrapada', visibility: 'Americas, Europe, Africa, W. Asia', sutak: '9 hours before', sparsha: 'Not visible from Nepal', madhya: 'Not visible from Nepal', moksha: 'Not visible from Nepal' },
                np: { name: 'आंशिक चन्द्रग्रहण', date: 'भाद्र १२, २०८३', time: '०३:५१ - ०८:३६ NPT', maxTime: '०६:१३ NPT', duration: '४ घण्टा ४५ मि.', rashi: 'कुम्भ', nakshatra: 'पूर्वभाद्रपद', visibility: 'अमेरिका, युरोप, अफ्रिका, पश्चिम एसिया', sutak: '९ घण्टा अगाडि', sparsha: 'नेपालबाट अदृश्य', madhya: 'नेपालबाट अदृश्य', moksha: 'नेपालबाट अदृश्य' },
                favorable: ['Mesha','Mithuna','Simha','Tula','Dhanu'], favorableNp: ['मेष','मिथुन','सिंह','तुला','धनु'],
                unfavorable: ['Kumbha','Karka','Vrishchika'], unfavorableNp: ['कुम्भ','कर्कट','वृश्चिक'],
                nepVisible: false,
              },
            ];

            const today = new Date();
            const solarAll = eclipses.filter(e => e.type.includes('solar'));
            const lunarAll = eclipses.filter(e => e.type.includes('lunar'));
            const upcomingSolar = solarAll.filter(e => new Date(e.date) >= today);
            const upcomingLunar = lunarAll.filter(e => new Date(e.date) >= today);
            const past = eclipses.filter(e => new Date(e.date) < today).reverse();

            const eclipseImg: Record<string, string> = {
              total_solar: '/images/eclipses/total-solar.svg',
              annular_solar: '/images/eclipses/annular-solar.svg',
              total_lunar: '/images/eclipses/total-lunar.svg',
              partial_lunar: '/images/eclipses/partial-lunar.svg',
            };

            // Plain-language "what is this" so a layperson understands each eclipse type
            const eclipseDesc: Record<string, { np: string; en: string }> = {
              total_solar: { np: 'चन्द्रमाले सूर्यलाई पूरै छोप्दा दिउँसै अँध्यारो हुन्छ।', en: 'The Moon fully covers the Sun — daytime turns dark.' },
              annular_solar: { np: 'चन्द्रमाले सूर्यको बीच भाग छोप्दा वरिपरि आगोको औंठी जस्तो देखिन्छ।', en: 'The Moon covers the Sun\'s centre, leaving a bright "ring of fire".' },
              total_lunar: { np: 'पृथ्वीको छायाँले चन्द्रमालाई पूरै छोप्दा चन्द्रमा रातो देखिन्छ।', en: 'Earth\'s shadow fully covers the Moon, turning it red (blood moon).' },
              partial_lunar: { np: 'पृथ्वीको छायाँले चन्द्रमाको केही भाग मात्र छोप्छ।', en: 'Earth\'s shadow covers only part of the Moon.' },
            };

            const daysUntil = (dateStr: string) => {
              const d = new Date(dateStr);
              const diff = Math.ceil((d.getTime() - today.getTime()) / (1000*60*60*24));
              if (diff < 0) return isNep ? 'बितिसकेको' : 'Past';
              if (diff === 0) return isNep ? 'आज' : 'Today';
              return isNep ? `${nepNum(String(diff))} दिन बाँकी` : `${diff} days away`;
            };

            const renderCard = (ecl: any, idx: number) => {
              const d = isNep ? ecl.np : ecl.en;
              const isPast = new Date(ecl.date) < today;
              const desc = eclipseDesc[ecl.type];
              return (
                <div key={idx} className={`rounded-lg border ${isPast ? 'border-gray-300 dark:border-gray-700 opacity-70' : 'border-orange-300 dark:border-orange-700'} bg-white/70 dark:bg-dark-900/30 overflow-hidden`}>
                  {/* Header */}
                  <div className={`px-3 py-2.5 flex items-center gap-3 ${isPast ? 'bg-gray-100 dark:bg-gray-800/50' : 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20'}`}>
                    <div className="shrink-0"><img src={eclipseImg[ecl.type]} alt={d.name} width={56} height={56} className="rounded-lg" /></div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-bold ${isPast ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'} ${isNep ? 'devanagari' : ''}`}>{d.name}</div>
                      <div className={`text-xs ${isPast ? 'text-gray-500' : 'text-orange-700 dark:text-orange-300'} ${isNep ? 'devanagari' : ''}`}>{d.date}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isPast ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400' : 'bg-orange-200 dark:bg-orange-800/50 text-orange-800 dark:text-orange-200'}`}>
                        {daysUntil(ecl.date)}
                      </span>
                    </div>
                  </div>

                  {/* Plain-language description */}
                  {desc && (
                    <div className={`px-3 py-2 text-xs text-gray-600 dark:text-gray-300 bg-gray-50/70 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-700/50 ${isNep ? 'devanagari' : ''}`}>
                      {isNep ? desc.np : desc.en}
                    </div>
                  )}

                  {/* Details */}
                  <div className="px-3 py-2 space-y-1.5 text-xs">
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      <div className="text-gray-500 dark:text-gray-400">{isNep ? 'कहिले (समय)' : 'When (time)'}</div>
                      <div className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.time}</div>
                      <div className="text-gray-500 dark:text-gray-400">{isNep ? 'सबैभन्दा बढी' : 'Peak'}</div>
                      <div className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.maxTime}</div>
                      <div className="text-gray-500 dark:text-gray-400">{isNep ? 'कति बेर' : 'How long'}</div>
                      <div className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.duration}</div>
                      <div className="text-gray-500 dark:text-gray-400">{isNep ? 'राशि' : 'Rashi'}</div>
                      <div className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.rashi}</div>
                      <div className="text-gray-500 dark:text-gray-400">{isNep ? 'नक्षत्र' : 'Nakshatra'}</div>
                      <div className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.nakshatra}</div>
                    </div>

                    {/* Eclipse phases: Sparsha / Madhya / Moksha */}
                    {(d.sparsha || d.madhya || d.moksha) && (
                      <div className="pt-1 border-t border-gray-100 dark:border-gray-700/50 space-y-1">
                        <div className={`text-[11px] font-bold text-gray-600 dark:text-gray-300 ${isNep ? 'devanagari' : ''}`}>{isNep ? 'ग्रहणका चरण' : 'Eclipse phases'}</div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">{isNep ? 'स्पर्श (सुरु)' : 'Sparsha (start)'}</span>
                          <span className={`font-medium text-gray-800 dark:text-gray-200 text-right ${isNep ? 'devanagari' : ''}`}>{d.sparsha}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">{isNep ? 'मध्य (चरम)' : 'Madhya (peak)'}</span>
                          <span className={`font-medium text-gray-800 dark:text-gray-200 text-right ${isNep ? 'devanagari' : ''}`}>{d.madhya}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">{isNep ? 'मोक्ष (अन्त्य)' : 'Moksha (end)'}</span>
                          <span className={`font-medium text-gray-800 dark:text-gray-200 text-right ${isNep ? 'devanagari' : ''}`}>{d.moksha}</span>
                        </div>
                      </div>
                    )}

                    {/* Sutak with layman explanation */}
                    <div className="pt-1 border-t border-gray-100 dark:border-gray-700/50">
                      <div className="flex justify-between gap-2">
                        <span className="text-gray-500 dark:text-gray-400">{isNep ? 'सूतक सुरु' : 'Sutak starts'}</span>
                        <span className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.sutak}</span>
                      </div>
                      <div className={`text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 ${isNep ? 'devanagari' : ''}`}>
                        {isNep ? 'सूतक = ग्रहण अघिको अशुभ समय; खानपान र शुभकार्य वर्जित।' : 'Sutak = the inauspicious window before an eclipse; avoid eating & auspicious work.'}
                      </div>
                    </div>

                    {/* Visibility */}
                    <div className="pt-1 border-t border-gray-100 dark:border-gray-700/50">
                      <div className="text-gray-500 dark:text-gray-400 mb-0.5">{isNep ? 'कहाँ देखिन्छ' : 'Where visible'}</div>
                      <div className={`font-medium text-gray-800 dark:text-gray-200 ${isNep ? 'devanagari' : ''}`}>{d.visibility}</div>
                      {ecl.nepVisible ? (
                        <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          {isNep ? '🇳🇵 नेपालबाट देखिन्छ' : '🇳🇵 Visible from Nepal'}
                        </span>
                      ) : (
                        <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400">
                          {isNep ? 'नेपालबाट देखिँदैन' : 'Not visible from Nepal'}
                        </span>
                      )}
                    </div>

                    {/* Favorable / Unfavorable Rashi */}
                    <div className="pt-1 border-t border-gray-100 dark:border-gray-700/50">
                      <div className="mb-1">
                        <span className={`text-green-600 dark:text-green-400 font-semibold ${isNep ? 'devanagari' : ''}`}>{isNep ? 'राम्रो फल पाउने राशि' : 'Favorable Rashi'}</span>
                        <div className={`flex flex-wrap gap-1 mt-0.5 ${isNep ? 'devanagari' : ''}`}>
                          {(isNep ? ecl.favorableNp : ecl.favorable).map((r: string, i: number) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-[10px] font-medium">{r}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className={`text-red-600 dark:text-red-400 font-semibold ${isNep ? 'devanagari' : ''}`}>{isNep ? 'सतर्क रहनुपर्ने राशि' : 'Unfavorable Rashi'}</span>
                        <div className={`flex flex-wrap gap-1 mt-0.5 ${isNep ? 'devanagari' : ''}`}>
                          {(isNep ? ecl.unfavorableNp : ecl.unfavorable).map((r: string, i: number) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-[10px] font-medium">{r}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            };

            const list = eclipseSubTab === 'surya' ? upcomingSolar : eclipseSubTab === 'chandra' ? upcomingLunar : past;
            const emptyMsg = eclipseSubTab === 'surya'
              ? (isNep ? 'आगामी सूर्यग्रहण छैन' : 'No upcoming solar eclipse')
              : eclipseSubTab === 'chandra'
              ? (isNep ? 'आगामी चन्द्रग्रहण छैन' : 'No upcoming lunar eclipse')
              : (isNep ? 'बितेका ग्रहण छैन' : 'No past eclipses');

            const subTabs: { key: 'surya' | 'chandra' | 'past'; label: string; icon: string }[] = [
              { key: 'surya', label: isNep ? 'सूर्य' : 'Solar', icon: '☀️' },
              { key: 'chandra', label: isNep ? 'चन्द्र' : 'Lunar', icon: '🌙' },
              { key: 'past', label: isNep ? 'बितेका' : 'Past', icon: '🕐' },
            ];

            return (
              <div className="space-y-3">
                {/* Sub-tab bar: Surya / Chandra / Past */}
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {subTabs.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setEclipseSubTab(t.key)}
                      className={`flex-1 py-2 text-xs sm:text-sm font-bold transition-colors ${
                        eclipseSubTab === t.key
                          ? 'bg-orange-500 text-white'
                          : 'bg-white dark:bg-dark-900/30 text-gray-600 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/10'
                      } ${isNep ? 'devanagari' : ''}`}
                    >
                      {t.icon} {t.label}
                      {t.key === 'past' && past.length > 0 && ` (${isNep ? nepNum(String(past.length)) : past.length})`}
                    </button>
                  ))}
                </div>

                {list.length === 0 ? (
                  <div className={`text-center py-6 px-3 rounded-lg bg-gray-50 dark:bg-gray-800/30 text-sm text-gray-500 dark:text-gray-400 ${isNep ? 'devanagari' : ''}`}>
                    {emptyMsg}
                  </div>
                ) : eclipseSubTab === 'past' ? (
                  // Past eclipses: compact collapsible rows (expand on click)
                  list.map((ecl) => {
                    const d = isNep ? ecl.np : ecl.en;
                    const isSolar = ecl.type.includes('solar');
                    const isOpen = expandedEclipse === ecl.date;
                    return (
                      <div key={ecl.date} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-dark-900/30 overflow-hidden">
                        <button
                          onClick={() => setExpandedEclipse(isOpen ? null : ecl.date)}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                        >
                          <span className="text-lg shrink-0">{isSolar ? '☀️' : '🌙'}</span>
                          <img src={eclipseImg[ecl.type]} alt={d.name} width={32} height={32} className="rounded shrink-0" />
                          <div className="flex-1 min-w-0 text-left">
                            <div className={`text-sm font-bold text-gray-800 dark:text-gray-100 truncate ${isNep ? 'devanagari' : ''}`}>{d.name}</div>
                            <div className={`text-xs text-gray-500 dark:text-gray-400 ${isNep ? 'devanagari' : ''}`}>{d.date}</div>
                          </div>
                          <span className={`shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
                        </button>
                        {isOpen && <div className="border-t border-gray-100 dark:border-gray-700/50">{renderCard(ecl, 0)}</div>}
                      </div>
                    );
                  })
                ) : (
                  list.map((ecl, idx) => renderCard(ecl, idx))
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

